import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';

import {getInstallationOrDefinitionId} from 'benchling-apps/lib';
import BenchlingConfig from 'BenchlingConfig';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {GRAY, GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import {AppDefinition, AppDefinitionVersion} from './BenchlingAppsPage';
import {DATE_FORMAT} from './BenchlingAppVersionHistory';
import CreateAppModal from './create-app/CreateAppModal';

export enum VersionCategory {
  Published,
  Drafts,
  History,
}

interface VersionHistoryItemProps {
  version: AppDefinitionVersion;
  isSelected: boolean;
  isCurrentlyInstalled: boolean;
  selectVersion: (version: AppDefinitionVersion) => void;
}

interface VersionCategoryHeaderProps {
  category: VersionCategory;
  showZero?: boolean;
}

export default function VersionHistoryItem(props: VersionHistoryItemProps): JSX.Element {
  function handleClick(): void {
    props.selectVersion(props.version);
  }
  const created = props.version.createdAt;
  const formatCreated = created ? (
    <div>Created on {moment(new Date(created)).format(DATE_FORMAT)}</div>
  ) : null;
  return (
    <div
      className={css(styles.listItem, styles.hover, props.isSelected ? styles.selected : null)}
      onClick={handleClick}
    >
      <div className={classNames(css(styles.versionName), css(styles.inlineBlock))}>
        Version {props.version.versionId}
      </div>
      {props.isCurrentlyInstalled && BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION ? (
        <Badge color={BadgeColor.BLUE} className={classNames('u-tiny-spacing-left', css(styles.inlineBlock))}>
          Installed
        </Badge>
      ) : (
        <></>
      )}
      {formatCreated}
    </div>
  );
}

export function VersionCategoryHeader(props: VersionCategoryHeaderProps): JSX.Element {
  const icon: BenchlingIconName = versionIcon[props.category];
  const label = `${versionLabel[props.category]}${props.showZero ? ' (0)' : ''}`;
  return (
    <div className={css(styles.listItem, styles.header)}>
      <BenchlingIcon icon={icon} className={css(styles.icon)} />
      {label}
    </div>
  );
}

export function VersionNewDraft({
  appDefinition,
  currentVersions,
  onRequestSubmit,
}: {
  appDefinition: AppDefinition;
  currentVersions: Array<AppDefinitionVersion>;
  onRequestSubmit: () => void;
}): JSX.Element {
  function openNewDraftModal(): void {
    legacyRunModal(CreateAppModal, {
      appId: getInstallationOrDefinitionId(appDefinition),
      currentVersions,
      onRequestSubmit,
    });
  }

  return (
    <div className={css(styles.listItem, styles.newDraft)}>
      <a onClick={openNewDraftModal}>+ Create a new draft</a>
    </div>
  );
}

const versionIcon: Record<VersionCategory, BenchlingIconName> = {
  [VersionCategory.Published]: 'star',
  [VersionCategory.Drafts]: 'clock-rotate-left',
  [VersionCategory.History]: 'paper-plane',
};

const versionLabel = {
  [VersionCategory.Published]: 'Currently Published',
  [VersionCategory.Drafts]: 'Drafts',
  [VersionCategory.History]: 'Publish History',
};

const styles = StyleSheet.create({
  listItem: {
    padding: 12,
    border: 'solid 1px',
    borderColor: GRAY,
    color: GRAY_DARKER,
    fontSize: 12,
    marginTop: -1,
    marginLeft: -1,
    marginRight: -1,
  },
  newDraft: {
    textAlign: 'center',
  },
  versionName: {
    fontSize: 14,
    color: NEAR_BLACK,
  },
  header: {
    textTransform: 'uppercase',
  },
  hover: {
    ':hover': {
      backgroundColor: GRAY_LIGHT,
      cursor: 'pointer',
    },
  },
  selected: {
    backgroundColor: GRAY_LIGHTER,
  },
  icon: {
    fontSize: 16,
    marginRight: 8,
  },
  inlineBlock: {
    display: 'inline-block',
  },
});
